import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button, EmptyState } from '../components/ui';
import { PaymentMethodSelect, PayMethod, CRYPTO_MIN_RUB } from '../components/PaymentMethodSelect';
import { useCartStore, useSettingsStore, useUserStore, useUIStore } from '../store';
import {
  formatPrice,
  isValidEmail,
  getRegionName,
  formatRegionalPrice,
  toRegionalPrice,
  findOptimalCards,
  getRegionalCurrency,
  resolveRate,
  formatPlatform,
} from '../utils/helpers';
import { api } from '../services/api';
import { BalanceInfo, CreateOrderPayload, Region } from '../types';
import { positionCaption } from '../utils/sharing';

const REGIONS: Region[] = ['TR', 'IN', 'UA'];
/** Полки корзины: регионы PS Store и шеринг (SH) — у него цены в рублях, без курса и режимов выдачи */
type Lane = Region | 'SH';
const LANES: Lane[] = ['TR', 'IN', 'UA', 'SH'];

const FlagTR: React.FC = () => (
  <svg width="22" height="21" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <rect width="18" height="17" rx="4" fill="#E30917" />
    <g transform="translate(0 -0.5) scale(0.5)">
      <path fill="#EEE" d="M16 24a6 6 0 1 1 0-12c1.31 0 2.52.425 3.507 1.138A7.332 7.332 0 0 0 14 10.647A7.353 7.353 0 0 0 6.647 18A7.353 7.353 0 0 0 14 25.354c2.195 0 4.16-.967 5.507-2.492A5.963 5.963 0 0 1 16 24zm3.913-5.77l2.44.562l.22 2.493l1.288-2.146l2.44.561l-1.644-1.888l1.287-2.147l-2.303.98l-1.644-1.889l.22 2.494z" />
    </g>
  </svg>
);

const FlagIN: React.FC = () => (
  <svg width="22" height="21" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <defs>
      <clipPath id="cartFlagIN">
        <rect width="18" height="17" rx="4" />
      </clipPath>
    </defs>
    <g clipPath="url(#cartFlagIN)">
      <rect width="18" height="5.67" fill="#F93" />
      <rect y="5.67" width="18" height="5.67" fill="#EEE" />
      <rect y="11.33" width="18" height="5.67" fill="#138808" />
      <g transform="translate(0 -0.5) scale(0.5)">
        <circle fill="navy" cx="18" cy="18" r="4" />
        <circle fill="#EEE" cx="18" cy="18" r="3" />
        <path fill="#6666B3" d="M18 15l.146 2.264l1.001-2.035l-.73 2.147l1.704-1.498l-1.497 1.705l2.147-.731l-2.035 1.002L21 18l-2.264.146l2.035 1.001l-2.147-.73l1.497 1.704l-1.704-1.497l.73 2.147l-1.001-2.035L18 21l-.146-2.264l-1.002 2.035l.731-2.147l-1.705 1.497l1.498-1.704l-2.147.73l2.035-1.001L15 18l2.264-.146l-2.035-1.002l2.147.731l-1.498-1.705l1.705 1.498l-.731-2.147l1.002 2.035z" />
        <circle fill="navy" cx="18" cy="18" r="1" />
      </g>
    </g>
  </svg>
);

const FlagUA: React.FC = () => (
  <svg width="22" height="21" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <defs><clipPath id="cartFlagUA"><rect width="18" height="17" rx="4" /></clipPath></defs>
    <g clipPath="url(#cartFlagUA)">
      <rect width="18" height="8.5" fill="#005BBB" />
      <rect y="8.5" width="18" height="8.5" fill="#FFD500" />
    </g>
  </svg>
);

const REGION_FLAG_COMPONENTS: Record<Region, React.FC> = {
  TR: FlagTR,
  IN: FlagIN,
  UA: FlagUA,
};

function pluralizeGoods(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'товар';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'товара';
  return 'товаров';
}

export const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    itemsByRegion,
    email,
    needNewAccount,
    agreedToTerms,
    promoCode,
    saveData,
    accountTarget,
    accountEmail,
    accountPassword,
    setEmail,
    setNeedNewAccount,
    setAgreedToTerms,
    setPromoCode,
    setSaveData,
    setAccountTarget,
    setAccountEmail,
    setAccountPassword,
    removeItem,
    updateQuantity,
    refreshStaleItems,
    refreshSharingItems,
  } = useCartStore();
  // Что изменилось на полке шеринга при сверке с сервером (убранные позиции, новые цены)
  const [sharingNotice, setSharingNotice] = useState<string | null>(null);
  const { settings } = useSettingsStore();

  // Лечим устаревшие снапшоты (без regionalPrice) при открытии корзины —
  // иначе регионалка «восстанавливается» из рублей и цены врут (550 ₽ → 197 TL).
  useEffect(() => {
    refreshStaleItems();
    refreshSharingItems().then(({ removed, repriced }) => {
      if (removed.length > 0) {
        setSharingNotice(`${removed.length === 1 ? 'Позиция' : 'Позиции'} ${removed.map((r) => `«${r}»`).join(', ')} больше ${removed.length === 1 ? 'недоступна и убрана' : 'недоступны и убраны'} из корзины`);
      } else if (repriced) {
        setSharingNotice('Цены позиций шеринга обновились — проверьте сумму перед оплатой');
      }
    }).catch(() => { /* сервер недоступен — оставляем как есть */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { addBalance, user, lastSelectedRegion, setLastSelectedRegion } = useUserStore();

  const setKeyboardOpen = useUIStore((s) => s.setKeyboardOpen);
  const isKeyboardOpen = useUIStore((s) => s.isKeyboardOpen);

  const [emailError, setEmailError] = useState('');
  const [accountEmailError, setAccountEmailError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PayMethod>('SBP');
  // Оплата внутренними баллами (кешбэк): до maxSpendSharePercent от итога
  const [balanceInfo, setBalanceInfo] = useState<BalanceInfo | null>(null);
  const [pointsToSpend, setPointsToSpend] = useState(0);
  // Кешбэк и списание — взаимоисключающие (бэкенд не начислит кешбэк, если
  // по заказу списаны баллы, применен промокод или реферальная скидка)
  const [pointsMode, setPointsMode] = useState<'accumulate' | 'spend'>('accumulate');

  useEffect(() => {
    api.getBalance().then(setBalanceInfo).catch(() => {});
  }, []);
  // Корзина только из карт пополнения (category 'topup') → по умолчанию «Выдачей кодов»,
  // кроме Украины (СНГ): там карты продаются как весь каталог — через аккаунт, по курсу
  // каталога и с ручной выдачей. Иначе: Индия → 'codes', Турция/СНГ → 'account'.
  const currentBucket = itemsByRegion[lastSelectedRegion || 'TR'] || [];
  const cartIsTopup = currentBucket.length > 0 && currentBucket.every((i) => i.product.category === 'topup');
  const defaultMode = (region: Region | null): 'account' | 'codes' =>
    region === 'UA' ? 'account' : (cartIsTopup || region === 'IN' ? 'codes' : 'account');
  const [fulfillmentMode, setFulfillmentMode] = useState<'account' | 'codes'>(
    defaultMode(lastSelectedRegion || 'TR')
  );

  // Полка шеринга показывается вместо региональной: сама, когда в регионе пусто,
  // или по выбору в переключателе полок
  const [sharingLane, setSharingLane] = useState(false);
  const sharingItems = itemsByRegion.SH || [];
  const isSharingLane = sharingLane && sharingItems.length > 0;
  useEffect(() => {
    const regionCount = (itemsByRegion[lastSelectedRegion || 'TR'] || []).length;
    if (!sharingLane && regionCount === 0 && sharingItems.length > 0) setSharingLane(true);
    if (sharingLane && sharingItems.length === 0) setSharingLane(false);
  }, [itemsByRegion, lastSelectedRegion, sharingLane, sharingItems.length]);

  useEffect(() => {
    if (!lastSelectedRegion) return;
    // Шеринг оформляется как «через аккаунт»: сумма — цены позиций в рублях
    setFulfillmentMode(isSharingLane ? 'account' : defaultMode(lastSelectedRegion));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSelectedRegion, cartIsTopup, isSharingLane]);

  useEffect(() => {
    if (!agreedToTerms) setAgreedToTerms(true);
  }, []);

  const isMobile = typeof window !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (!isMobile) return;
    setKeyboardOpen(true);
    const el = e.target;
    setTimeout(() => {
      const root = document.getElementById('root');
      if (!root) return;
      const elRect = el.getBoundingClientRect();
      const viewportH = window.visualViewport?.height ?? window.innerHeight;
      const overflow = elRect.bottom - viewportH + 60;
      if (overflow > 0) {
        root.scrollBy({ top: overflow, behavior: 'smooth' });
      }
    }, 500);
  }, [setKeyboardOpen, isMobile]);
  const handleBlur = useCallback(() => {
    if (!isMobile) return;
    setTimeout(() => setKeyboardOpen(false), 150);
  }, [setKeyboardOpen, isMobile]);

  useEffect(() => {
    return () => setKeyboardOpen(false);
  }, [setKeyboardOpen]);

  // Active cart region = global selected region
  const cartRegion: Region = lastSelectedRegion || 'TR';
  const items = useMemo(
    () => (isSharingLane ? sharingItems : (itemsByRegion[cartRegion] || [])),
    [isSharingLane, sharingItems, itemsByRegion, cartRegion],
  );
  // Курс считаем ПО КАЖДОМУ товару: override товара для региона, иначе глобальный.
  // Регионалка не меняется → подбор карт остается корректным.
  const exchangeRate = settings.exchangeRates[cartRegion] || 1; // глобальный курс региона (fallback регионалки)
  // Курс региона для режима «выдача кодов» (карты/переплата): codesRates, иначе плоский.
  const regionCodesRate = (region: Region): number => {
    const cr = settings.codesRates?.[region];
    return (typeof cr === 'number' && cr > 0) ? cr : (settings.exchangeRates[region] || 1);
  };
  const activeCodesRate = regionCodesRate(cartRegion);
  // Эффективный курс товара по режиму: account = диапазон по регионалке, codes = курс кодов.
  // override товара приоритетнее в обоих режимах.
  const effRateForItem = (
    product: { rateOverrides?: Record<string, number> },
    region: Region,
    regional: number,
    mode: 'account' | 'codes',
  ): number => resolveRate(region, regional, mode, product.rateOverrides, settings);
  const currency = getRegionalCurrency(cartRegion);

  // Compute totals for all regions (used by switcher).
  // Для регионов с fulfillment='codes' (IN) показываем стоимость карты пополнения,
  // а не базовую цену товара.
  const regionTotals = useMemo(() => {
    const totals: Record<Lane, { count: number; totalRub: number }> = {
      TR: { count: 0, totalRub: 0 },
      IN: { count: 0, totalRub: 0 },
      UA: { count: 0, totalRub: 0 },
      // Шеринг: цены позиций в рублях как есть
      SH: {
        count: (itemsByRegion.SH || []).reduce((sum, i) => sum + i.quantity, 0),
        totalRub: (itemsByRegion.SH || []).reduce((sum, i) => sum + i.variant.price * i.quantity, 0),
      },
    };
    for (const r of REGIONS) {
      const bucket = itemsByRegion[r] || [];
      const globalRate = settings.exchangeRates[r] || 1;
      const totalRegional = bucket.reduce((sum, item) => {
        const regional = item.variant.regionalPrice
          ? item.variant.regionalPrice
          : toRegionalPrice(item.variant.price, globalRate);
        return sum + regional * item.quantity;
      }, 0);
      // account: сумма по каждому товару своим диапазонным курсом по регионалке.
      // Нет regionalPrice → сохраненная рублевая цена как есть (без пересчета).
      const accountRub = bucket.reduce((sum, item) => {
        const regional = item.variant.regionalPrice;
        if (!regional || regional <= 0) return sum + Math.ceil(item.variant.price / 5) * 5 * item.quantity;
        const rate = effRateForItem(item.product, r, regional, 'account');
        return sum + Math.ceil((regional * rate) / 5) * 5 * item.quantity;
      }, 0);
      // Дефолт-режим региона: карты пополнения (topup, кроме Украины) или Индия → 'codes'
      // (стоимость карты), иначе 'account' (цена товара).
      const bucketIsTopup = bucket.length > 0 && bucket.every((i) => i.product.category === 'topup');
      const usesCards = (bucketIsTopup && r !== 'UA') || r === 'IN';
      let totalRub = accountRub;
      if (usesCards && bucket.length > 0) {
        const denoms = settings.cardDenominations?.[r] || [];
        if (denoms.length > 0) {
          const codesRate = regionCodesRate(r);
          const denomsWithRate = denoms.map((d) => ({ ...d, priceRub: Math.ceil((d.nominal * codesRate) / 5) * 5 }));
          const card = findOptimalCards(Math.ceil(totalRegional), denomsWithRate);
          // итог = стоимость купленных карт пополнения
          totalRub = card.totalRub;
        }
      }
      totals[r] = {
        count: bucket.reduce((s, i) => s + i.quantity, 0),
        totalRub,
      };
    }
    return totals;
  }, [itemsByRegion, settings.exchangeRates, settings.rangePricing, settings.codesRates, settings.cardDenominations]);

  const regionsWithItems = useMemo(
    () => LANES.filter((r) => (itemsByRegion[r] || []).length > 0),
    [itemsByRegion]
  );

  // Calculate order total in regional currency (for active region)
  const orderTotalRegional = useMemo(() => {
    return items.reduce(
      (sum, item) => {
        const rp = item.variant.regionalPrice;
        const regional = rp ? rp : toRegionalPrice(item.variant.price, exchangeRate);
        return sum + regional * item.quantity;
      },
      0
    );
  }, [items, exchangeRate]);

  // Find optimal card combination
  const cardResult = useMemo(() => {
    if (items.length === 0) return null;
    const denominations = settings.cardDenominations[cartRegion];
    if (!denominations || denominations.length === 0) return null;
    const denomsWithRate = denominations.map(d => ({
      ...d,
      priceRub: Math.ceil((d.nominal * activeCodesRate) / 5) * 5,
    }));
    return findOptimalCards(Math.ceil(orderTotalRegional), denomsWithRate);
  }, [cartRegion, items, orderTotalRegional, settings.cardDenominations, activeCodesRate]);

  // Через аккаунт: цена = сумма по каждому товару своим диапазонным курсом по регионалке.
  // Нет regionalPrice (ручная цена / старый снапшот в корзине) → берем сохраненную
  // рублевую цену КАК ЕСТЬ, без пересчета через курс (иначе 550 ₽ превращается в 435 ₽).
  const accountTotalRub = useMemo(() => {
    return items.reduce((sum, item) => {
      const rp = item.variant.regionalPrice;
      if (!rp || rp <= 0) return sum + Math.ceil(item.variant.price / 5) * 5 * item.quantity;
      const rate = effRateForItem(item.product, cartRegion, rp, 'account');
      return sum + Math.ceil((rp * rate) / 5) * 5 * item.quantity;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, cartRegion, settings.exchangeRates, settings.rangePricing]);

  // Выдача кодов: итог = стоимость купленных карт пополнения (совпадает с разбивкой ниже).
  const codesTotalRub = useMemo(() => {
    if (!cardResult) return 0;
    return cardResult.totalRub;
  }, [cardResult]);

  // Fallback на сумму товаров, пока подбор карт не готов (настройки грузятся) — иначе «0 ₽».
  const displayTotalRub = fulfillmentMode === 'account' ? accountTotalRub : (codesTotalRub || accountTotalRub);

  // Лимит оплаты баллами: min(баланс, доля от итога). Сервер дополнительно
  // пересчитает и срежет от суммы ПОСЛЕ скидки промокода.
  const referralEnabled = settings.referral?.enabled ?? false;
  const maxSpendShare = settings.referral?.maxSpendSharePercent ?? 50;
  const maxPoints = useMemo(() => {
    if (!balanceInfo || balanceInfo.frozen || !referralEnabled) return 0;
    return Math.min(balanceInfo.balance, Math.floor((displayTotalRub * maxSpendShare) / 100));
  }, [balanceInfo, displayTotalRub, maxSpendShare, referralEnabled]);
  const effectivePoints = Math.min(pointsToSpend, maxPoints);

  useEffect(() => {
    // Итог изменился (промокод/количество) — держим списание в актуальном лимите
    setPointsToSpend(pointsMode === 'spend' ? maxPoints : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxPoints, pointsMode]);

  // Промокод и баллы — что-то одно: с примененным промокодом списывать нельзя
  useEffect(() => {
    if (promoSuccess && pointsMode === 'spend') setPointsMode('accumulate');
  }, [promoSuccess, pointsMode]);

  // Сколько вернется баллами, если копить. С промокодом кешбэк не начисляется.
  const cashbackPercent = balanceInfo?.loyalty?.currentPercent ?? settings.referral?.cashbackPercent ?? 3;
  const cashbackPreview = useMemo(() => {
    if (!referralEnabled || promoSuccess) return 0;
    return Math.floor((displayTotalRub * cashbackPercent) / 100);
  }, [referralEnabled, promoSuccess, displayTotalRub, cashbackPercent]);

  // Крипта недоступна ниже минималки Cardlink (~$10) — считаем от суммы деньгами
  const moneyPayable = Math.max(0, displayTotalRub - effectivePoints);
  const cryptoDisabled = moneyPayable < CRYPTO_MIN_RUB;
  useEffect(() => {
    if (cryptoDisabled && paymentMethod === 'CRYPTO') setPaymentMethod('SBP');
  }, [cryptoDisabled, paymentMethod]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !isValidEmail(value)) {
      setEmailError('Введите корректный email');
    } else {
      setEmailError('');
    }
  };

  const handleAccountEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccountEmail(value);
    if (value && !isValidEmail(value)) {
      setAccountEmailError('Введите корректный email');
    } else {
      setAccountEmailError('');
    }
  };

  const isAccountOwnMode = !isSharingLane && fulfillmentMode === 'account' && accountTarget === 'own';
  const isAccountNewMode = !isSharingLane && fulfillmentMode === 'account' && accountTarget === 'new';
  const effectiveEmail = isAccountOwnMode ? accountEmail : email;
  const effectiveEmailError = isAccountOwnMode ? accountEmailError : emailError;

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const result = await api.validatePromo(promoCode.trim(), user?.id);
      if (result.valid) {
        setPromoSuccess(`Промокод применен: скидка ${result.discount}%`);
        setPromoError('');
      } else {
        setPromoError(result.error || 'Промокод не найден');
        setPromoSuccess('');
      }
    } catch {
      setPromoError('Промокод не найден');
      setPromoSuccess('');
    } finally {
      setPromoChecking(false);
    }
  };

  const handleCheckout = async () => {
    if (isAccountOwnMode) {
      if (!accountEmail) {
        setAccountEmailError('Email обязателен для заказа');
        return;
      }
      if (!isValidEmail(accountEmail)) {
        setAccountEmailError('Введите корректный email');
        return;
      }
      if (!accountPassword) return;
    } else if (fulfillmentMode === 'codes') {
      // Выдача кодов: email необязателен. Если введен — проверяем формат.
      if (email && !isValidEmail(email)) {
        setEmailError('Введите корректный email');
        return;
      }
    } else {
      if (!email) {
        setEmailError('Email обязателен для заказа');
        return;
      }
      if (!isValidEmail(email)) {
        setEmailError('Введите корректный email');
        return;
      }
    }
    if (!agreedToTerms) return;
    if (items.length === 0) return;
    if (fulfillmentMode === 'codes' && !cardResult) return;

    setIsProcessing(true);
    try {
      const payload: CreateOrderPayload = isSharingLane
        // Шеринг: регион SH, без режима выдачи и карт — бэкенд считает сумму по ценам позиций
        ? {
            items: items.map(i => ({ variantId: i.variant.id, quantity: i.quantity })),
            email,
            region: 'SH',
            needNewAccount: false,
            cardBreakdown: [],
            totalNominal: 0,
            overpay: 0,
            promoCode: pointsMode === 'spend' ? undefined : (promoCode || undefined),
            saveData,
            pointsToSpend: effectivePoints > 0 ? effectivePoints : undefined,
            paymentMethod,
          }
        : fulfillmentMode === 'codes'
        ? {
            items: items.map(i => ({ variantId: i.variant.id, quantity: i.quantity })),
            email,
            region: cartRegion,
            needNewAccount,
            cardBreakdown: cardResult!.cards,
            totalNominal: cardResult!.totalNominal,
            overpay: cardResult!.overpay,
            // При оплате баллами промокод не действует — не отправляем его вовсе
            promoCode: pointsMode === 'spend' ? undefined : (promoCode || undefined),
            saveData,
            pointsToSpend: effectivePoints > 0 ? effectivePoints : undefined,
            paymentMethod,
          }
        : {
            items: items.map(i => ({ variantId: i.variant.id, quantity: i.quantity })),
            email: effectiveEmail,
            region: cartRegion,
            needNewAccount: accountTarget === 'new',
            cardBreakdown: [],
            totalNominal: 0,
            overpay: 0,
            // При оплате баллами промокод не действует — не отправляем его вовсе
            promoCode: pointsMode === 'spend' ? undefined : (promoCode || undefined),
            saveData,
            pointsToSpend: effectivePoints > 0 ? effectivePoints : undefined,
            paymentMethod,
            fulfillmentMode: 'account',
            customerAccount: accountTarget === 'own'
              ? { target: 'own', email: accountEmail, password: accountPassword }
              : { target: 'new' },
          };

      const order = await api.createOrder(payload);
      // Промокод ушел в заказ — в корзине его больше не держим, иначе он висит
      // в поле месяцами и подставляется в следующие заказы
      setPromoCode('');

      if (fulfillmentMode === 'codes' && cardResult && cardResult.overpay > 0) {
        addBalance(cartRegion, cardResult.overpay, currency);
      }

      // New flow: bot sends payment link in chat, just close the mini app
      if ((order as any).useBotFlow) {
        try {
          (window as any).Telegram?.WebApp?.close?.();
        } catch { /* ignore */ }
        return;
      }

      navigate(`/payment/${order.id}`, {
        state: {
          paymentUrl: order.paymentUrl || '',
          stubPayAvailable: order.stubPayAvailable || false,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      if (message.includes('промокод')) {
        setPromoError(message);
        setPromoSuccess('');
      } else {
        alert(`Ошибка при создании заказа: ${message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Region switcher — shown if more than one region has items, or current is empty but others aren't
  const showSwitcher = regionsWithItems.length > 1 || (items.length === 0 && regionsWithItems.length > 0);

  // Итог сверки полки шеринга с сервером — над корзиной, чтобы было видно и после того,
  // как убранная позиция была последней и полка шеринга исчезла
  const sharingNoticeBlock = sharingNotice ? (
    <div className="mb-4 rounded-2xl p-3.5 text-[13px] leading-[18px]" style={{ background: '#FFF7E6', color: '#8A4B00' }}>
      {sharingNotice}
    </div>
  ) : null;

  const switcherBlock = showSwitcher ? (
    <div className="mb-5">
      <h3 className="mb-2" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
        Корзины по регионам
      </h3>
      <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4" style={{ gap: '8px' }}>
        {LANES.map((r) => {
          const t = regionTotals[r];
          const isActive = r === 'SH' ? isSharingLane : (!isSharingLane && r === cartRegion);
          if (!isActive && t.count === 0) return null;
          return (
            <button
              key={r}
              onClick={() => { if (r === 'SH') setSharingLane(true); else { setSharingLane(false); setLastSelectedRegion(r); } }}
              className="flex-shrink-0 rounded-[14px] active:scale-[0.97] transition-all"
              style={{
                minWidth: '155px',
                padding: '10px 12px',
                border: `1.5px solid ${isActive ? '#3657F0' : 'rgba(129, 131, 132, 0.5)'}`,
                background: 'rgba(129, 131, 132, 0.10)',
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                {r === 'SH' ? (
                  <span className="inline-flex w-5 h-5 rounded-md items-center justify-center text-white text-[10px] font-extrabold" style={{ background: '#3657F0', fontFamily: 'Manrope, sans-serif' }}>П</span>
                ) : React.createElement(REGION_FLAG_COMPONENTS[r])}
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1A1D1A' }}>
                  {r === 'SH' ? 'Шеринг' : getRegionName(r)}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 500, color: '#818384' }}>
                  {t.count > 0 ? `${t.count} ${pluralizeGoods(t.count)}` : 'пусто'}
                </span>
                {t.totalRub > 0 && (
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', fontWeight: 700, color: isActive ? '#3657F0' : '#1A1D1A' }}>
                    {formatPrice(t.totalRub)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  if (items.length === 0) {
    return (
      <Layout>
        <div className="px-4 py-6">
          <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>Корзина</h1>
          {sharingNoticeBlock}
          {switcherBlock}
          <div className="flex items-center justify-center" style={{ minHeight: regionsWithItems.length > 0 ? '300px' : 'calc(100vh - 220px)' }}>
            <EmptyState
              title="Корзина пуста"
              description={regionsWithItems.length > 0 ? 'Переключитесь на другой регион или добавьте товары' : 'Добавьте товары в корзину, чтобы оформить заказ'}
              icon={
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M11.236 5.33333C11.6084 4.54667 12.4117 4 13.3401 4H18.6599C19.5883 4 20.3902 4.54667 20.764 5.33333C21.6723 5.34133 22.3812 5.38267 23.0143 5.63067C23.77 5.92703 24.4273 6.43068 24.9108 7.084C25.3989 7.74267 25.629 8.58667 25.9429 9.748L26.9297 13.3773L27.3021 14.4987L27.334 14.5387C28.5323 16.0773 27.9618 18.3653 26.8207 22.94C26.0945 25.8507 25.7327 27.3053 24.6501 28.1533C23.5675 29 22.0713 29 19.0789 29H12.9211C9.92867 29 8.43245 29 7.34985 28.1533C6.26726 27.3053 5.90417 25.8507 5.17934 22.94C4.03822 18.3653 3.46766 16.0773 4.66597 14.5387L4.69789 14.4987L5.07028 13.3773L6.05712 9.748C6.37232 8.58667 6.60241 7.74133 7.08918 7.08267C7.57293 6.42984 8.23016 5.92666 8.98572 5.63067C9.61879 5.38267 10.3263 5.34 11.236 5.33333ZM11.2387 7.33733C10.3583 7.34667 10.0045 7.38 9.71189 7.49467C9.30487 7.65424 8.95089 7.92548 8.69047 8.27733C8.45639 8.59333 8.31807 9.03467 7.93238 10.4573L7.1743 13.2427C8.53087 13 10.3849 13 12.9198 13H19.0789C21.6151 13 23.4678 13 24.8244 13.24L24.0676 10.4547C23.6819 9.032 23.5436 8.59067 23.3095 8.27467C23.0491 7.92281 22.6951 7.65157 22.2881 7.492C21.9955 7.37733 21.6417 7.344 20.7613 7.33467C20.5725 7.73313 20.2748 8.06976 19.9029 8.30552C19.531 8.54128 19.1 8.6665 18.6599 8.66667H13.3401C12.9002 8.66662 12.4693 8.54159 12.0974 8.30608C11.7255 8.07057 11.4277 7.73555 11.2387 7.33733Z" fill="#818384"/>
                </svg>
              }
              action={{
                label: 'Перейти в каталог',
                onClick: () => navigate('/home'),
              }}
            />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`px-4 py-6 ${isKeyboardOpen ? 'pb-12' : 'pb-24'}`}>
        {sharingNoticeBlock}
        {switcherBlock}
        {/* Fulfillment mode selector — только для Турции */}
        {cartRegion === 'TR' && !isSharingLane && (
          <div className="mb-5">
            <h2 className="mb-2" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
              Как оформить заказ?
            </h2>
            <div className="grid grid-cols-2 gap-2 p-1 bg-background-secondary rounded-2xl border border-[#E1E3E5]">
              <button
                onClick={() => setFulfillmentMode('account')}
                className="py-2.5 rounded-xl text-[14px] font-semibold transition-all"
                style={
                  fulfillmentMode === 'account'
                    ? { background: 'linear-gradient(135deg, #5370F5, #3657F0)', color: '#fff' }
                    : { background: 'transparent', color: '#1A1D1A' }
                }
              >
                Через аккаунт
              </button>
              <button
                onClick={() => setFulfillmentMode('codes')}
                className="py-2.5 rounded-xl text-[14px] font-semibold transition-all"
                style={
                  fulfillmentMode === 'codes'
                    ? { background: 'linear-gradient(135deg, #5370F5, #3657F0)', color: '#fff' }
                    : { background: 'transparent', color: '#1A1D1A' }
                }
              >
                Выдачей кодов
              </button>
            </div>
            <p className="mt-2 text-[13px] text-text-secondary">
              {fulfillmentMode === 'account'
                ? 'Зайдем в аккаунт и оформим покупку'
                : 'Выдадим коды для пополнения кошелька'}
            </p>
          </div>
        )}

        {/* Block 1: Header with totals */}
        <div className="mb-5">
          <div className="flex items-baseline justify-between mb-1">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>Корзина</h1>
            {displayTotalRub > 0 && (
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '24px', lineHeight: '120%', letterSpacing: '-0.4px', color: '#3657F0' }}>
                {formatPrice(displayTotalRub)}
              </span>
            )}
          </div>
        </div>

        {/* Block 2: Card denominations (только для режима 'codes') */}
        {fulfillmentMode === 'codes' && cardResult && cardResult.cards.length > 0 && (
          <div className="mb-5 space-y-2.5">
            <div>
              <div className="flex items-baseline justify-between">
                <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>Карты пополнения</h3>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '15px', lineHeight: '20px', color: '#3657F0' }}>
                  {formatRegionalPrice(cardResult.totalNominal, cartRegion)}
                </span>
              </div>
              <p className="mt-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>Регион: {getRegionName(cartRegion)}</p>
            </div>
            {cardResult.cards.map((card) => (
              <div
                key={card.denomination}
                className="flex items-center gap-3 bg-white rounded-2xl p-3 border border-gray-200/80"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-primary leading-none">
                    {card.denomination}
                  </span>
                  <span className="text-[9px] font-medium text-primary/70 leading-none mt-0.5">
                    {currency}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                    {formatPrice(card.priceRub * card.count)}
                  </p>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>
                    Номинал {card.denomination} {currency}
                  </p>
                </div>

                <span className="px-2.5 py-1 bg-primary/10 rounded-lg flex-shrink-0" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '13px', lineHeight: '16px', color: '#3657F0' }}>
                  {card.count} шт.
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Block 3: Card sufficiency (только для режима 'codes') */}
        {fulfillmentMode === 'codes' && cardResult && cardResult.totalNominal >= Math.ceil(orderTotalRegional) && (
          <div className="mb-5 bg-primary/10 rounded-2xl p-4">
            <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#3657F0' }}>
              Вы покупаете карту на {formatRegionalPrice(cardResult.totalNominal, cartRegion)}. Этого хватит для оплаты выбранных товаров в PS Store.
            </p>
          </div>
        )}

        {/* Block 4: Product list */}
        <div className="mb-5">
          <div className="flex items-baseline justify-between mb-2.5">
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>Выбранные товары</h3>
            {fulfillmentMode === 'codes' && (
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '15px', lineHeight: '20px', color: '#3657F0' }}>
                {formatRegionalPrice(orderTotalRegional, cartRegion)}
              </span>
            )}
          </div>
          <div className="space-y-2.5">
            {items.map((item) => (
              <div
                key={item.variant.id}
                className="flex items-center gap-3 bg-white rounded-2xl p-3 border border-gray-200/80"
              >
                <img
                  src={item.product.coverImage}
                  alt={item.product.name}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'https://via.placeholder.com/64x64?text=PS';
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="truncate" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                    {item.product.name}
                  </p>
                  {/* Версии «для PS4» и «для PS4/PS5» одной игры в корзине иначе неотличимы */}
                  {item.variant.platform && item.product.category === 'game' && (
                    <p className="truncate" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '16px', color: '#818384' }}>
                      {formatPlatform(item.variant.platform)}
                    </p>
                  )}
                  {/* Позиция шеринга: «П3 (PS5)» — у подписки просто консоль */}
                  {item.product.category === 'sharing' && item.variant.position && (
                    <p className="truncate" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '16px', color: '#818384' }}>
                      {positionCaption(item.product, item.variant)}
                    </p>
                  )}
                  {fulfillmentMode === 'codes' && (
                    <p className="mt-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>
                      Цена в PS Store {formatRegionalPrice(item.variant.regionalPrice || toRegionalPrice(item.variant.price, exchangeRate), cartRegion)}
                    </p>
                  )}
                  {item.product.category === 'topup' && (
                    <div className="mt-2 inline-flex items-center gap-2.5 bg-gray-100 rounded-full p-1">
                      <button
                        onClick={() => updateQuantity(item.variant.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-95 transition disabled:opacity-40"
                        aria-label="Уменьшить количество"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#1A1D1A" strokeWidth={2}><path strokeLinecap="round" d="M5 12h14" /></svg>
                      </button>
                      <span className="min-w-[18px] text-center" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '15px', color: '#1A1D1A' }}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.variant.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-95 transition"
                        aria-label="Увеличить количество"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#1A1D1A" strokeWidth={2}><path strokeLinecap="round" d="M12 5v14M5 12h14" /></svg>
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeItem(item.variant.id)}
                  className="p-1.5 text-text-tertiary hover:text-red-500 transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Block 5: Overpay info (только для режима 'codes'; не для корзины из карт пополнения) */}
        {fulfillmentMode === 'codes' && !cartIsTopup && cardResult && cardResult.overpay > 0 && (
          <div className="mb-3">
            <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384', maxWidth: '320px' }}>
              Выбранные товары обойдутся в{' '}
              <span style={{ color: '#3657F0' }}>
                {formatRegionalPrice(orderTotalRegional, cartRegion)}
              </span>.
              {' '}Не потраченные{' '}
              <span style={{ color: '#3657F0' }}>
                {formatRegionalPrice(cardResult.overpay, cartRegion)}
              </span>
              {' '}останутся на вашем аккаунте для будущих покупок.
            </p>
          </div>
        )}

        {/* Block 5: Checkout form */}
        <div className="space-y-4">
          {isSharingLane ? (
            <>
              <div className="rounded-2xl p-3.5 text-[13px] leading-[18px]" style={{ background: '#EEF4FF', color: '#1E3A8A' }}>
                Шеринг выдается вручную: после оплаты напишите нам в поддержку — пришлем данные аккаунта и инструкцию.
              </div>
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => setKeyboardOpen(true)}
                  onBlur={() => setKeyboardOpen(false)}
                  placeholder="Введите E-mail для чека"
                  className={`w-full px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-[#1A1D1A] placeholder:text-[#B0B3BA] outline-none transition-colors ${emailError ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-primary/30'}`}
                />
                {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
              </div>
            </>
          ) : fulfillmentMode === 'account' ? (
            <>
              {/* Account flow: target selector */}
              <div>
                <h3 className="mb-2" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                  Куда оформить заказ?
                </h3>
                <div className="grid grid-cols-2 gap-2 p-1 bg-background-secondary rounded-2xl border border-[#E1E3E5]">
                  <button
                    onClick={() => setAccountTarget('own')}
                    className="py-2.5 rounded-xl text-[14px] font-semibold transition-all"
                    style={
                      accountTarget === 'own'
                        ? { background: 'linear-gradient(135deg, #5370F5, #3657F0)', color: '#fff' }
                        : { background: 'transparent', color: '#1A1D1A' }
                    }
                  >
                    На мой аккаунт
                  </button>
                  <button
                    onClick={() => setAccountTarget('new')}
                    className="py-2.5 rounded-xl text-[14px] font-semibold transition-all"
                    style={
                      accountTarget === 'new'
                        ? { background: 'linear-gradient(135deg, #5370F5, #3657F0)', color: '#fff' }
                        : { background: 'transparent', color: '#1A1D1A' }
                    }
                  >
                    У меня нет аккаунта
                  </button>
                </div>
                <p className="mt-2 text-[13px] text-text-secondary">
                  {accountTarget === 'own'
                    ? `У меня есть ${cartRegion === 'TR' ? 'турецкий' : cartRegion === 'IN' ? 'индийский' : 'Украина (СНГ)'} аккаунт, оформите на него`
                    : 'Мы бесплатно создадим для Вас аккаунт и сразу оформим на него заказ'}
                </p>
              </div>

              {accountTarget === 'own' ? (
                <>
                  <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                    Данные аккаунта
                  </h3>
                  <div>
                    <input
                      type="email"
                      value={accountEmail}
                      onChange={handleAccountEmailChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      placeholder="Введите e-mail"
                      autoComplete="email"
                      className={`w-full px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors ${
                        accountEmailError ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-primary/30'
                      }`}
                    />
                    {accountEmailError && (
                      <p className="text-xs text-red-500 mt-1">{accountEmailError}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={accountPassword}
                      onChange={(e) => setAccountPassword(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      placeholder="Введите пароль"
                      autoComplete="off"
                      spellCheck={false}
                      className="w-full px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                        saveData ? 'bg-primary border-primary' : 'border-gray-300'
                      }`}
                      onClick={() => setSaveData(!saveData)}
                    >
                      {saveData && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>
                      Запомнить данные для следующих заказов
                    </span>
                  </label>
                </>
              ) : (
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder="Введите E-mail для чека"
                    className={`w-full px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors ${
                      emailError ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-primary/30'
                    }`}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1">{emailError}</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Email */}
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="Введите E-mail для чека"
                  className={`w-full px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors ${
                    emailError ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-primary/30'
                  }`}
                />
                {emailError && (
                  <p className="text-xs text-red-500 mt-1">{emailError}</p>
                )}
              </div>

              {/* Account checkbox */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    needNewAccount ? 'bg-primary border-primary' : 'border-gray-300'
                  }`}
                  onClick={() => setNeedNewAccount(!needNewAccount)}
                >
                  {needNewAccount && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>
                  Мне нужен новый аккаунт PlayStation
                </span>
              </label>
            </>
          )}

          {/* Payment method */}
          <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} cryptoDisabled={cryptoDisabled} />

          {/* Promo code */}
          <div>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setPromoError('');
                  setPromoSuccess('');
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={pointsMode === 'spend'}
                placeholder="Ввести промокод"
                className={`flex-1 px-4 py-3.5 rounded-2xl bg-background-secondary border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors ${
                  promoError ? 'ring-2 ring-red-400' : promoSuccess ? 'ring-2 ring-green-400' : 'focus:ring-2 focus:ring-primary/30'
                }`}
              />
              <button
                onClick={handleValidatePromo}
                className={`px-4 py-3 rounded-2xl border border-[#E1E3E5] transition-colors flex-shrink-0 ${
                  promoCode && !promoChecking && pointsMode !== 'spend'
                    ? 'bg-primary text-white active:bg-primary/90'
                    : 'bg-background-secondary text-text-tertiary'
                }`}
                disabled={!promoCode || promoChecking || pointsMode === 'spend'}
              >
                {promoChecking ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                )}
              </button>
            </div>
            {promoError && (
              <p className="text-xs text-red-500 mt-1">{promoError}</p>
            )}
            {promoSuccess && (
              <p className="text-xs text-green-600 mt-1">{promoSuccess}</p>
            )}
            {pointsMode === 'spend' && (
              <p className="text-xs text-text-tertiary mt-1">Промокод не действует при оплате баллами — выберите что-то одно</p>
            )}
          </div>

          {/* Баллы: копить или списать — одно из двух */}
          {referralEnabled && balanceInfo && (balanceInfo.balance > 0 || cashbackPreview > 0) && (
            <div
              className="p-4"
              style={{ borderRadius: '16px', border: '1px solid #E1E3E5', background: '#FFFFFF' }}
            >
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[17px] font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1D1A' }}>
                  {balanceInfo.balance.toLocaleString('ru-RU')} баллов
                </span>
                {pointsMode === 'accumulate' && cashbackPreview > 0 && (
                  <span className="text-[13px]" style={{ color: '#8B8E97' }}>
                    +{cashbackPreview.toLocaleString('ru-RU')} за этот заказ
                  </span>
                )}
              </div>

              <div className="flex gap-1.5 p-1 rounded-2xl" style={{ background: '#F4F4F7' }}>
                {([
                  ['accumulate', 'Накапливать'],
                  ['spend', 'Списать все'],
                ] as const).map(([mode, label]) => {
                  const active = pointsMode === mode;
                  const disabled = mode === 'spend' && (maxPoints <= 0 || Boolean(promoSuccess));
                  return (
                    <button
                      key={mode}
                      onClick={() => { if (!disabled) setPointsMode(mode); }}
                      disabled={disabled}
                      className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold transition-colors"
                      style={{
                        background: active ? '#3657F0' : 'transparent',
                        color: active ? '#FFFFFF' : disabled ? '#B0B3BA' : '#33415C',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {balanceInfo.frozen ? (
                <p className="text-[12.5px] mt-2" style={{ color: '#E5484D' }}>
                  Баланс на проверке — списание временно недоступно
                </p>
              ) : pointsMode === 'spend' ? (
                <p className="text-[12.5px] mt-2" style={{ color: '#33415C' }}>
                  Спишется {effectivePoints.toLocaleString('ru-RU')} баллов, кешбэк за этот заказ не начислится
                </p>
              ) : promoSuccess ? (
                <p className="text-[12.5px] mt-2" style={{ color: '#8B8E97' }}>
                  С промокодом баллы не списываются и кешбэк не начисляется — действует что-то одно
                </p>
              ) : (
                <p className="text-[12.5px] mt-2" style={{ color: '#8B8E97' }}>
                  Баллы вернутся на баланс после оплаты. 1 балл = 1 ₽
                </p>
              )}
            </div>
          )}

          {/* Terms checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5 ${
                agreedToTerms ? 'bg-primary border-primary' : 'border-gray-300'
              }`}
              onClick={() => setAgreedToTerms(!agreedToTerms)}
            >
              {agreedToTerms && (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '16px', color: '#818384' }}>
              Согласен с условиями{' '}
              <span onClick={() => navigate('/terms')} style={{ color: '#3657F0', fontWeight: 500, cursor: 'pointer' }}>
                Условий использования
              </span>
              {' '}и{' '}
              <span onClick={() => navigate('/privacy')} style={{ color: '#3657F0', fontWeight: 500, cursor: 'pointer' }}>
                Политики конфиденциальности
              </span>
            </span>
          </label>
        </div>

        {/* Order button */}
        <div className={`fixed left-0 right-0 bg-white/95 backdrop-blur-lg px-4 py-3 z-30 transition-all duration-300 ease-in-out ${
          isKeyboardOpen ? 'translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
        }`} style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}>
          <Button
            onClick={handleCheckout}
            disabled={isProcessing || !!effectiveEmailError || (fulfillmentMode !== 'codes' && !effectiveEmail) || (isAccountOwnMode && !accountPassword) || !agreedToTerms}
            fullWidth
            size="lg"
          >
            {isProcessing ? 'Обработка...' : 'Оформить заказ'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};
